import {useEffect, useState, useRef} from 'react'
import InterpolationController from "./InterpolationController";

export interface InterpolationMetadata {
    minValue: number
    maxValue: number
    extent: number[] // [minLon, minLat, maxLon, maxLat]
    width: number
    height: number
}

interface UseInterpolationResult {
    imageUrl: string | null
    metadata: InterpolationMetadata | null
    loading: boolean
    error: string | null
}

/**
 * Hook to fetch IDW interpolation PNG from the server
 */
export function useInterpolation(
    programId: number | undefined,
    amendment: string,
    year: string,
    enabled: boolean = true
): UseInterpolationResult {
    const [imageUrl, setImageUrl] = useState<string | null>(null)
    const [metadata, setMetadata] = useState<InterpolationMetadata | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // Track the current object URL to clean up
    const currentUrlRef = useRef<string | null>(null)

    useEffect(() => {
        // Track if this effect is still active (not superseded by a newer one)
        let isActive = true
        // Store the previous URL to clean up AFTER setting the new one
        const previousUrl = currentUrlRef.current

        if (!enabled || !programId) {
            // Clean up and reset
            if (previousUrl) {
                URL.revokeObjectURL(previousUrl)
                currentUrlRef.current = null
            }
            setImageUrl(null)
            setMetadata(null)
            setError(null)
            return
        }

        const fetchInterpolation = async () => {
            setLoading(true)
            setError(null)

            try {
                
                const apiUrl =  InterpolationController.urls.getInterpolation(programId, amendment, year);
                const response = await fetch(apiUrl)

                // Check if this effect is still active
                if (!isActive) return

                if (!response.ok) {
                    if (response.status === 404) {
                        setImageUrl(null)
                        setMetadata(null)
                        return
                    }
                    throw new Error(`Failed to fetch interpolation: ${response.statusText}`)
                }

                // Parse metadata from headers
                const minValue = parseFloat(response.headers.get('X-Min-Value') || '0')
                const maxValue = parseFloat(response.headers.get('X-Max-Value') || '0')
                const extentStr = response.headers.get('X-Extent') || ''
                const width = parseInt(response.headers.get('X-Width') || '0', 10)
                const height = parseInt(response.headers.get('X-Height') || '0', 10)

                const extent = extentStr.split(',').map(parseFloat).filter(n => !isNaN(n))

                const meta = {
                    minValue,
                    maxValue,
                    extent: extent.length === 4 ? extent : [],
                    width,
                    height
                }

                // Get blob and create object URL
                const blob = await response.blob()

                // Check again if still active
                if (!isActive) return

                const newUrl = URL.createObjectURL(blob)

                // Update state with new URL first, then clean up old one
                currentUrlRef.current = newUrl
                setMetadata(meta)
                setImageUrl(newUrl)

                // Now safe to revoke the previous URL
                if (previousUrl) {
                    URL.revokeObjectURL(previousUrl)
                }
            } catch (err) {
                if (!isActive) return
                setError(err instanceof Error ? err.message : 'Unknown error')
                setImageUrl(null)
                setMetadata(null)
            } finally {
                if (isActive) {
                    setLoading(false)
                }
            }
        }

        fetchInterpolation()

        // Cleanup on unmount or when dependencies change
        return () => {
            isActive = false
            // Don't revoke here - let the next effect handle it
        }
    }, [programId, amendment, year, enabled])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (currentUrlRef.current) {
                URL.revokeObjectURL(currentUrlRef.current)
                currentUrlRef.current = null
            }
        }
    }, [])

    return {imageUrl, metadata, loading, error}
}

export default useInterpolation
